import type { AgentThreadMessage } from './agent-thread-message.model'
import type { AgentThreadEvent } from './agent-thread-event.model'
import type {
  AgentChartBlock,
  AgentMessageCapabilitiesResponse,
  AgentMessageOptions,
  AiThreadQueuedMessageResponse
} from '@awork/features/agent/services/agent-service/types'

export enum MessageStatus {
  NotStarted = 'not-started',
  Pending = 'pending',
  Done = 'done',
  AwaitingUserInput = 'awaiting-user-input'
}

export interface Message extends AgentMessageCapabilitiesResponse {
  id: string
  clientMessageId?: string
  role: 'user' | 'agent' | 'tool'
  content: string
  createdOn?: Date | string
  assistantName?: string
  temporaryFileIds?: string[]
  temporaryFiles?: TemporaryFileReference[]
  useCompanySearch?: boolean
  toolCallId?: string
  toolCalls?: ToolCall[]
  toolCallGroups?: ToolCallGroup[]
  cost?: number
  model?: string
  inputTokens?: number
  outputTokens?: number
  reasoningTokens?: number
  status?: MessageStatus
  loadingMessage?: string
  userQuestion?: UserQuestion
  messageOptions?: AgentMessageOptions
  reasoning?: MessageReasoning
  toolActivities?: MessageToolActivity[]
  contextCompactions?: MessageContextCompaction[]
  runtimeWarnings?: MessageRuntimeWarning[]
}

export interface MessageToolActivity {
  id: string
  toolName: string
  title: string
  status: 'running' | 'done' | 'error'
  result?: string
  error?: string
}

export interface MessageContextCompaction {
  id: string
  status: 'started' | 'completed' | 'failed'
  summary?: string
  error?: string
  sequence?: number
  createdOn: Date | string
}

export interface MessageReasoning {
  messageId?: string
  text: string
  isThinking: boolean
  durationMs: number
  segmentStartedOn?: string
  segmentBaseLength?: number
}

export interface MessageRuntimeWarning {
  code: string
  reason?: string
  missingAccessLabels?: string[]
  sourceId?: string
  sourceName?: string
  credentialScope?: string
  sequence?: number
}

export interface TemporaryFileReference {
  id: string
  name?: string
  mimeType?: string
  downloadUrl?: string
}

/** One question of a question card. */
export interface UserQuestionItem {
  /** Question text in Markdown. */
  question: string
  /** Answers the user can choose. */
  options: string[]
  /** Whether the user can choose more than one option. */
  multiSelect: boolean
  /** Whether the user can type their own answer. */
  allowCustomText: boolean
}

export interface UserQuestion {
  /** First question, or the label of a connector, skill access, or WhatsApp card. */
  question: string
  options: string[]
  allowCustomText: boolean
  /** All questions of the card, in order. A card asks one to four questions. */
  questions: UserQuestionItem[]
  mcpConnectionAction?: McpConnectionAction
  skillAccessAction?: SkillAccessAction
  whatsAppLink?: boolean
}

export interface SkillAccessAction {
  skillId: string
  agentId?: string
}

export interface McpConnectionAction {
  connectorId: string
  connectionId?: string
  connectorName: string
  action: 'enable' | 'reconnect'
}

export interface ToolCall {
  id: string
  toolName: string
  arguments: Record<string, string>
  response?: string
  chartBlock?: AgentChartBlock
}

interface ToolCallGroup {
  id: string
  displayName: string
  description: string
  status: 'success' | 'error' | 'partial'
  toolCalls: ToolCall[]
  summary: string
}

/**
 * Batch confirmation action describing a single operation type
 */
export interface ConfirmationAction {
  actionDescriptionEn: string
  actionDescriptionDe: string
  itemNames: string[]
  /** When set, the item names start hidden behind a toggle with this label. */
  itemsToggleLabelEn?: string | null
  itemsToggleLabelDe?: string | null
  itemCount: number
  hasMoreItems: boolean
}

/**
 * Batch confirmation details containing all information for the confirmation dialog
 */
export interface ConfirmationDetails {
  headerMessageEn: string
  headerMessageDe: string
  /** Text below the header that explains the action. */
  descriptionEn?: string | null
  descriptionDe?: string | null
  actions: ConfirmationAction[]
  confirmationQuestionEn: string
  confirmationQuestionDe: string
  confirmButtonTextEn: string
  confirmButtonTextDe: string
  cancelButtonTextEn: string
  cancelButtonTextDe: string
  isDestructive: boolean
  totalItemCount: number
}

/**
 * Batch confirmation state for a message
 */
export interface BatchConfirmation {
  confirmationId: string
  isDestructive: boolean
  affectedItemCount: number
  confirmationDetails?: ConfirmationDetails
  message?: string
}

export enum Rating {
  Positive = 'positive',
  Negative = 'negative'
}

export type AgentThreadType = 'personal-agent' | 'custom-agent'

export interface UsedConnector {
  id: string
  name: string
  logoPath?: string
}

export interface AgentThreadContributor {
  id: string
  userId: string
  accessLevel: 'read' | 'manage'
}

export interface AgentThreadViewerAccess {
  canRead: boolean
  canWrite: boolean
  isJoined: boolean
  canJoin: boolean
  canSend: boolean
  canStop: boolean
  canManageThread: boolean
  canManageSharing: boolean
  canLeave: boolean
  sendBlockedReason?: string
}

export interface AgentThreadShareTarget {
  id: string
  targetType: 'user' | 'team' | 'workspace'
  targetId: string
  canWrite: boolean
  createdOn: string
}

export interface AgentThreadSharing {
  ownerUserId: string
  shares: AgentThreadShareTarget[]
  viewerAccess: AgentThreadViewerAccess
}

export type AgentThreadShareRequestTarget = Pick<AgentThreadShareTarget, 'targetType' | 'targetId' | 'canWrite'>

export interface UpdateAgentThreadSharingRequest {
  shares: AgentThreadShareRequestTarget[]
}

export interface AgentCreditUsage {
  creditsUsed: number
  lastUsageOn: string | null
}

export interface IAgentThread {
  id: string
  type: AgentThreadType
  messages: Message[]
  totalCost?: number
  usage?: AgentCreditUsage | null
  rating?: Rating
  createdOn?: Date | string
  createdBy?: string
  updatedOn?: Date | string
  updatedBy?: string
  title?: string
  status: string
  modelProvider?: string
  modelName?: string
  agentId: string
  agentNameSnapshot?: string | null
  executionOwnerUserId?: string | null
  executionTriggeredByUserId?: string | null
  contributorUserIds: string[]
  executionType?: string | null
  executionSourceId?: string | null
  parentThreadId?: string
  projectId?: string
  taskId?: string
  documentId?: string
  startedOn?: string | null
  completedOn?: string
  lastActivityOn?: string
  lastActivityBy?: string
  failureReason?: string
  lastSequence: number
  latestThreadUpdateSequence: number
  hasUpdate: boolean
  isUnseen?: boolean
  isReadOnly: boolean
  isShared: boolean
  viewerAccess?: AgentThreadViewerAccess
  isArchived?: boolean
  archivedOn?: string | null
  isPinned?: boolean
  pinnedOrder?: number | null
  listStateUpdatedOn?: string | null
  isAwaitingAction?: boolean
  contextWindowUsageRatio?: number
  usedConnectors: UsedConnector[]
  contributors: AgentThreadContributor[]
  queue?: AiThreadQueuedMessageResponse[] | null
}

/**
 * Shared model for personal-agent and custom-agent threads.
 */
export class AgentThread implements IAgentThread {
  /** Last complete server snapshot, used while a reopened timeline refreshes. */
  persistedSnapshot?: { messages: AgentThreadMessage[]; events: AgentThreadEvent[] }
  id: string
  type: AgentThreadType
  messages: Message[]
  totalCost?: number
  usage?: AgentCreditUsage | null
  rating?: Rating
  createdOn?: Date | string
  createdBy?: string
  updatedOn?: Date | string
  updatedBy?: string
  title?: string
  status: string
  modelProvider?: string
  modelName?: string
  agentId: string
  agentNameSnapshot?: string | null
  executionOwnerUserId?: string | null
  executionTriggeredByUserId?: string | null
  contributorUserIds: string[]
  executionType?: string | null
  executionSourceId?: string | null
  parentThreadId?: string
  projectId?: string
  taskId?: string
  documentId?: string
  startedOn?: string | null
  completedOn?: string
  lastActivityOn?: string
  lastActivityBy?: string
  failureReason?: string
  lastSequence: number
  latestThreadUpdateSequence: number
  hasUpdate: boolean
  isUnseen?: boolean
  isReadOnly: boolean
  isShared: boolean
  viewerAccess?: AgentThreadViewerAccess
  isArchived?: boolean
  archivedOn?: string | null
  isPinned?: boolean
  pinnedOrder?: number | null
  listStateUpdatedOn?: string | null
  isAwaitingAction?: boolean
  contextWindowUsageRatio?: number
  usedConnectors: UsedConnector[]
  contributors: AgentThreadContributor[]
  queue?: AiThreadQueuedMessageResponse[] | null

  constructor(thread: Partial<IAgentThread>) {
    Object.assign(this, {
      messages: [],
      type: thread.agentId ? 'custom-agent' : 'personal-agent',
      status: 'ready',
      agentId: '',
      contributorUserIds: [],
      lastSequence: 0,
      latestThreadUpdateSequence: 0,
      hasUpdate: false,
      isReadOnly: false,
      isShared: false,
      ...thread,
      usedConnectors: thread.usedConnectors ?? [],
      contributors: thread.contributors ?? []
    })
  }
}
